import { useState } from 'react'
import { ui } from '../styles/ui'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { useTranslation } from '../contexts/TranslationContext'
import { formatDateTime } from '../utils/date'

/**
 * Displays step-by-step execution logs for a single job.
 * Each log entry can be expanded to see its input/output JSON.
 */
export default function JobLogsPanel({ logs = [], loading = false }) {
  const [expanded, setExpanded] = useState(new Set())
  const { t } = useTranslation()

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className={ui.spinner} />
      </div>
    )
  }

  if (!logs.length) {
    return (
      <div className={`${ui.card} ${ui.cardBody} text-sm text-muted-foreground`}>
        {t('No logs found for this job')}
      </div>
    )
  }

  return (
    <div className={`${ui.card} divide-y divide-divider`}>
      {logs.map((log) => {
        const isOpen = expanded.has(log.id)
        const hasDetail = log.input != null || log.output != null || log.error
        return (
          <div key={log.id}>
            <button
              type="button"
              onClick={() => hasDetail && toggle(log.id)}
              className={`flex w-full items-start gap-3 px-4 py-3 text-left ${hasDetail ? 'hover:bg-surface-elevated cursor-pointer' : 'cursor-default'}`}
            >
              {/* Step expand indicator */}
              <span className="mt-0.5 shrink-0 text-muted-foreground">
                {hasDetail ? (isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />) : <span className="w-3.5 inline-block" />}
              </span>

              {/* Status dot */}
              <span
                className={`mt-1 inline-block h-2 w-2 shrink-0 rounded-full ${
                  log.error ? 'bg-red-500' : 'bg-green-500'
                }`}
              />

              {/* Step name + meta */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-foreground">{log.step}</span>
                  {log.type && log.type !== 'log' && (
                    <span className={`${ui.badge} ${ui.badgeNeutral}`}>{log.type}</span>
                  )}
                  {log.durationMs != null && (
                    <span className={`${ui.badge} ${ui.badgeNeutral}`}>{log.durationMs} ms</span>
                  )}
                  {log.error && (
                    <span className={`${ui.badge} ${ui.badgeError}`}>error</span>
                  )}
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {formatDateTime(log.createdAt)}
                </div>
              </div>
            </button>

            {/* Expanded detail */}
            {isOpen && (
              <div className="bg-surface-elevated px-4 pb-4 space-y-3">
                {log.error && (
                  <div>
                    <div className="text-xs font-semibold uppercase text-red-600 mb-1">{t('Error')}</div>
                    <pre className="rounded bg-red-50 p-3 text-xs text-red-800 overflow-x-auto whitespace-pre-wrap">{log.error}</pre>
                  </div>
                )}
                {log.input != null && (
                  <div>
                    <div className="text-xs font-semibold uppercase text-muted-foreground mb-1">{t('Input')}</div>
                    <pre className="rounded bg-surface border border-outline p-3 text-xs text-foreground overflow-x-auto whitespace-pre-wrap">
                      {JSON.stringify(log.input, null, 2)}
                    </pre>
                  </div>
                )}
                {log.output != null && (
                  <div>
                    <div className="text-xs font-semibold uppercase text-muted-foreground mb-1">{t('Output')}</div>
                    <pre className="rounded bg-surface border border-outline p-3 text-xs text-foreground overflow-x-auto whitespace-pre-wrap">
                      {JSON.stringify(log.output, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

